/**
 * Agent Color Identities
 * 
 * Each agent has a unique color drawn from a physical or cinematic material reference.
 * These communicate the agent's role through material origin.
 * 
 * COLOR STATES:
 * - Idle:     agent color at 15-18% opacity (agent present, not speaking)
 * - Hover:    agent color at 45% opacity (agent available)
 * - Active:   agent color at 100% opacity (agent currently working)
 * - Selected: agent color at 100% + box-shadow glow at 15-20% opacity
 * 
 * CRITICAL RULES:
 * - Glow on active/selected is the ONLY decorative effect permitted
 * - NO gradients on nodes
 * - NO left-border color strips on chat bubbles or node cards
 * - NO color fills on idle nodes
 */

export type AgentType = 
  | 'director' 
  | 'character' 
  | 'object' 
  | 'location' 
  | 'storyboard' 
  | 'scriptwriter' 
  | 'video'

export interface AgentColorConfig {
  /** Material reference description */
  material: string
  /** Primary hex color (100% opacity) */
  hex: string
  /** Dim variant for dark backgrounds */
  dim: string
  /** Text color when on active background */
  text: string
  /** Idle state - 15-18% opacity */
  idle: string
  /** Hover state - 45% opacity */
  hover: string
  /** Focus state - 70% opacity (between hover and active) */
  focus: string
  /** Active state - 100% opacity */
  active: string
  /** Box-shadow glow for selected state */
  glow: string
  /** Idle opacity percentage (for reference) */
  idleOpacity: number
}

export const AGENT_COLORS: Record<AgentType, AgentColorConfig> = {
  director: {
    material: 'Aged brass — Nagra IV-S tape recorder faceplate, 1972',
    hex: '#C9952A',
    dim: '#6B4E0E',
    text: '#F5E4C0',
    idle: 'rgba(201,149,42,0.18)',
    hover: 'rgba(201,149,42,0.45)',
    focus: 'rgba(201,149,42,0.70)',
    active: '#C9952A',
    glow: '0 0 20px 5px rgba(201,149,42,0.20)',
    idleOpacity: 18,
  },
  character: {
    material: 'Bolex H16 lens coating — precision optical glass teal, 1965',
    hex: '#2AB5A0',
    dim: '#0A4A42',
    text: '#C0F0E8',
    idle: 'rgba(42,181,160,0.15)',
    hover: 'rgba(42,181,160,0.45)',
    focus: 'rgba(42,181,160,0.70)',
    active: '#2AB5A0',
    glow: '0 0 18px 4px rgba(42,181,160,0.18)',
    idleOpacity: 15,
  },
  object: {
    material: 'Oxidised iron — rust of a well-used film prop',
    hex: '#C4622A',
    dim: '#5C2008',
    text: '#F5D8C0',
    idle: 'rgba(196,98,42,0.15)',
    hover: 'rgba(196,98,42,0.45)',
    focus: 'rgba(196,98,42,0.70)',
    active: '#C4622A',
    glow: '0 0 16px 4px rgba(196,98,42,0.16)',
    idleOpacity: 15,
  },
  location: {
    material: 'Aged brass — Nagra IV-S tape recorder faceplate, 1972',
    hex: '#C9952A',
    dim: '#6B4E0E',
    text: '#F5E4C0',
    idle: 'rgba(201,149,42,0.18)',
    hover: 'rgba(201,149,42,0.45)',
    focus: 'rgba(201,149,42,0.70)',
    active: '#C9952A',
    glow: '0 0 20px 5px rgba(201,149,42,0.20)',
    idleOpacity: 18,
  },
  storyboard: {
    material: 'Diazo blueprint paper — production design drawings, faded indigo',
    hex: '#3A7FC4',
    dim: '#0A2848',
    text: '#C0D8F5',
    idle: 'rgba(58,127,196,0.15)',
    hover: 'rgba(58,127,196,0.45)',
    focus: 'rgba(58,127,196,0.70)',
    active: '#3A7FC4',
    glow: '0 0 20px 5px rgba(58,127,196,0.20)',
    idleOpacity: 15,
  },
  scriptwriter: {
    material: 'Worn velvet — 1950s cinema seat, Odeon Leicester Square',
    hex: '#8A6AC4',
    dim: '#2E1A5C',
    text: '#E0D0F8',
    idle: 'rgba(138,106,196,0.15)',
    hover: 'rgba(138,106,196,0.45)',
    focus: 'rgba(138,106,196,0.70)',
    active: '#8A6AC4',
    glow: '0 0 16px 4px rgba(138,106,196,0.16)',
    idleOpacity: 15,
  },
  video: {
    material: 'Moviola light — Steenbeck ST900 flatbed editor backlit screen, 1975',
    hex: '#D4703A',
    dim: '#5C2008',
    text: '#F8D8C0',
    idle: 'rgba(212,112,58,0.18)',
    hover: 'rgba(212,112,58,0.45)',
    focus: 'rgba(212,112,58,0.70)',
    active: '#D4703A',
    glow: '0 0 22px 6px rgba(212,112,58,0.22)',
    idleOpacity: 18,
  },
}

/**
 * Get agent color config by type
 */
export function getAgentColor(agentType: AgentType): AgentColorConfig {
  return AGENT_COLORS[agentType]
}

/**
 * Get border color based on agent type and state
 */
export function getAgentBorderColor(
  agentType: AgentType | undefined,
  state: 'idle' | 'hover' | 'active' | 'selected'
): string {
  if (!agentType) return 'transparent'
  const config = AGENT_COLORS[agentType]
  
  switch (state) {
    case 'idle':
      return config.idle
    case 'hover':
      return config.hover
    case 'active':
    case 'selected':
      return config.active
    default:
      return config.idle
  }
}
